// app/app.routes.ts
import { Routes } from '@angular/router';
import { AuthGuard } from './core/guards/auth.guard';
import { EmailVerifiedGuard } from './core/guards/email-verified.guard';

export const routes: Routes = [
  {
    path: 'privacy-policy',
    loadComponent: () =>
      import('./features/security/privacy-policy/privacy-policy.component')
        .then(m => m.PrivacyPolicyComponent)
  },
  {
    path: 'terms',
    loadComponent: () =>
      import('./features/security/terms-of-service/terms-of-service.component')
        .then(m => m.TermsOfServiceComponent)
  },
  {
    path: 'response-invite-landing/:invitationId',
    loadComponent: () =>
      import('./features/organization/organization-people/response-invite-landing/response-invite-landing.component')
        .then(m => m.ResponseInviteLandingComponent)
  },
  {
    path: 'organization/new',
    canActivate: [AuthGuard, EmailVerifiedGuard],
    loadComponent: () =>
      import('./features/organization/create-organization-and-project/create-organization-and-project.component')
        .then(m => m.CreateOrganizationAndProjectComponent)
  },
  {
    path: 'organizations',
    canActivate: [AuthGuard, EmailVerifiedGuard],
    loadComponent: () =>
      import('./features/organization/organizations/organizations.component')
        .then(m => m.OrganizationsComponent)
  },
  // Stripe Checkout callback routes (for popup window)
  {
    path: 'checkout/success',
    loadComponent: () =>
      import('./features/organization/organization-billing/stripe-checkout-callback/stripe-checkout-success.component')
        .then(m => m.StripeCheckoutSuccessComponent)
  },
  {
    path: 'checkout/cancel',
    loadComponent: () =>
      import('./features/organization/organization-billing/stripe-checkout-callback/stripe-checkout-cancel.component')
        .then(m => m.StripeCheckoutCancelComponent)
  },
  {
    path: 'organization/:organizationId',
    canActivate: [AuthGuard, EmailVerifiedGuard],
    children: [
      {
        path: 'projects/new',
        loadComponent: () =>
          import('./features/projects/create-project/create-project.component')
            .then(m => m.CreateProjectComponent)
      },
      {
        path: 'projects/:projectId/upload-spec',
        loadComponent: () =>
          import('./features/projects/upload-spec/upload-spec.component')
            .then(m => m.UploadSpecComponent)
      },
      {
        path: 'projects/:projectId/upload-markdown',
        loadComponent: () =>
          import('./features/projects/upload-markdown/upload-markdown.component')
            .then(m => m.UploadMarkdownComponent)
      },
      {
        path: 'projects/:projectId',
        loadComponent: () =>
          import('./shared/components/main-layout/project-layout/project-layout.component')
            .then(m => m.ProjectLayoutComponent),
        children: getProjectChildren()
      },
      {
        // With Organization sidebar and toolbar
        path: '',
        loadComponent: () =>
          import('./shared/components/main-layout/organization-layout/organization-layout.component')
            .then(m => m.OrganizationLayoutComponent),
        children: getOrganizationChildren()
      },
    ]
  },
  {
    path: 'help',
    loadComponent: () =>
      import('./features/help/help.component')
        .then(m => m.HelpComponent)
  },
  {
    path: 'docs',
    loadComponent: () =>
      import('./features/doc-viewer/doc-viewer-landing/doc-viewer-landing.component')
        .then(m => m.DocViewerLandingComponent),
    children: [
      {
        path: '', // root: /docs
        loadComponent: () =>
          import('./features/doc-viewer/markdown-viewer/markdown-viewer.component')
            .then(m => m.MarkdownViewerComponent)
      },
      {
        path: ':versionName', // /docs/:versionName
        loadComponent: () =>
          import('./features/doc-viewer/markdown-viewer/markdown-viewer.component')
            .then(m => m.MarkdownViewerComponent)
      }
    ]
  },
  {
    path: 'api-reference',
    loadComponent: () =>
      import('./features/doc-viewer/doc-viewer-landing/doc-viewer-landing.component')
        .then(m => m.DocViewerLandingComponent),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/api-reference/api-reference.component')
            .then(m => m.ApiReferenceComponent)
      },
      {
        path: ':versionName',
        loadComponent: () =>
          import('./features/api-reference/api-reference.component')
            .then(m => m.ApiReferenceComponent)
      },
      {
        path: ':versionName/:endpoint',
        loadComponent: () =>
          import('./features/api-reference/api-reference.component')
            .then(m => m.ApiReferenceComponent)
      }
    ]
  },
  {
    path: 'auth',
    children: getAuthChildren()
  },
  {
    path: '',
    loadComponent: () =>
      import('./features/auth/auth-redirect/auth-redirect.component')
        .then(m => m.AuthRedirectComponent),
    pathMatch: 'full'
  },
];

function getAuthChildren(): Routes | undefined {
  return [
    {
      path: 'sign-in',
      loadComponent: () =>
        import('./features/auth/sign-in/sign-in.component')
          .then(m => m.SignInComponent)
    },
    {
      path: 'sign-up',
      loadComponent: () =>
        import('./features/auth/sign-up/sign-up.component')
          .then(m => m.SignUpComponent)
    },
    {
      path: 'forgot-password',
      loadComponent: () =>
        import('./features/auth/forgot-password/forgot-password.component')
          .then(m => m.ForgotPasswordComponent)
    },
    {
      path: 'activate-email',
      loadComponent: () =>
        import('./features/auth/activate-email/activate-email.component')
          .then(m => m.ActivateEmailComponent)
    }
  ];
}

function getOrganizationChildren(): Routes {
  return [
    {
      path: 'overview',
      loadComponent: () =>
        import('./features/organization/organization-overview/organization-overview.component')
          .then(m => m.OrganizationOverviewComponent)
    },
    {
      path: 'settings',
      loadComponent: () =>
        import('./features/organization/organization-settings/organization-settings.component')
          .then(m => m.OrganizationSettingsComponent)
    },
    {
      path: 'billing',
      loadComponent: () =>
        import('./features/organization/organization-billing/organization-billing.component')
          .then(m => m.OrganizationBillingComponent),
      children: [
        {
          path: '',
          loadComponent: () =>
            import('./features/organization/organization-billing/pricing-plans/organization-pricing-plans.component')
              .then(m => m.OrganizationPricingPlansComponent)
        },
        {
          path: 'history',
          loadComponent: () =>
            import('./features/organization/organization-billing/payment-history/organization-payment-history.component')
              .then(m => m.OrganizationPaymentHistoryComponent)
        },
      ]
    },
    {
      path: 'people',
      loadComponent: () =>
        import('./features/organization/organization-people/organization-people.component')
          .then(m => m.OrganizationPeopleComponent),
      children: [
        {
          path: '',
          loadComponent: () =>
            import('./features/organization/organization-people/people-list/organization-people-list.component')
              .then(m => m.OrganizationPeopleListComponent)
        },
        {
          path: 'invitations',
          loadComponent: () =>
            import('./features/organization/organization-people/invitations/organization-invitations.component')
              .then(m => m.OrganizationInvitationsComponent)
        },
      ]
    },
    {
      path: 'api-keys',
      loadComponent: () =>
        import('./features/organization/organization-api-keys/organization-api-keys.component')
          .then(m => m.OrganizationApiKeysComponent)
    },
    {
      path: 'support',
      loadComponent: () =>
        import('./features/organization/organization-support/organization-support.component')
          .then(m => m.OrganizationSupportComponent)
    },
  ];
}

function getProjectChildren(): Routes {
  return [
    {
      path: 'overview/check-spec/:uploadIntentId',
      loadComponent: () =>
        import('./features/projects/project-overview/project-overview.component')
          .then(m => m.ProjectOverviewComponent)
    },
    {
      path: 'overview',
      loadComponent: () =>
        import('./features/projects/project-overview/project-overview.component')
          .then(m => m.ProjectOverviewComponent),
      children: [
        {
          path: '',
          loadComponent: () =>
            import('./features/projects/project-overview/project-overview-specs/project-overview-specs.component')
              .then(m => m.ProjectOverviewSpecsComponent)
        },
        {
          path: 'spec-validation-results/:uploadIntentId',
          loadComponent: () =>
            import('./features/projects/project-overview/project-overview-validations/project-overview-validations.component')
              .then(m => m.ProjectOverviewValidationsComponent)
        }
      ]
    },
    {
      path: 'markdown/check-markdown/:uploadIntentId',
      loadComponent: () =>
        import('./features/projects/project-markdown/project-markdown.component')
          .then(m => m.ProjectMarkdownComponent)
    },
    {
      path: 'markdown',
      loadComponent: () =>
        import('./features/projects/project-markdown/project-markdown.component')
          .then(m => m.ProjectMarkdownComponent),
      children: [
        {
          path: '',
          loadComponent: () =>
            import('./features/projects/project-markdown/project-markdown-versions/project-markdown-versions.component')
              .then(m => m.ProjectMarkdownVersionsComponent)
        },
        {
          path: 'markdown-validation-results/:uploadIntentId',
          loadComponent: () =>
            import('./features/projects/project-markdown/project-markdown-validations/project-markdown-validations.component')
              .then(m => m.ProjectMarkdownValidationsComponent)
        }
      ]
    },
    {
      path: 'requests',
      loadComponent: () =>
        import('./features/projects/project-requests/project-requests.component')
          .then(m => m.ProjectRequestsComponent)
    },
    {
      path: 'settings',
      loadComponent: () =>
        import('./features/projects/project-settings/project-settings.component')
          .then(m => m.ProjectSettingsComponent),
      children: [
        {
          path: '',
          loadComponent: () =>
            import('./features/projects/project-settings/project-settings-general/project-settings-general.component')
              .then(m => m.ProjectSettingsGeneralComponent)
        },
        {
          path: 'visual',
          loadComponent: () =>
            import('./features/projects/project-settings/project-settings-visual/project-settings-visual.component')
              .then(m => m.ProjectSettingsVisualComponent)
        }
      ]
    },
  ];
}